/**
 * Pure derivations for RightPanels (workspace list, impact, tests) from feedItems.
 */
import type { FeedItem } from "@/stores/devAgentStore";

const FILE_FETCH_TOOLS = new Set([
  "read_source_code",
  "get_class_detail",
  "get_metadata_by_name",
  "get_method_detail",
  "get_object_fields",
  "get_sub_nodes",
  "retrieve_metadata",
  "get_quality_report",
  "graph_calls",
  "graph_impact",
  "graph_dependents",
  "graph_triggers_on",
  "graph_data_access",
  "graph_object_usage",
]);

export type ArtifactFileRow = {
  id: string;
  name: string;
  metadataType?: string;
  summary?: string;
  operation?: string;
};

function _artifactDisplayName(art: {
  id: string;
  name?: string;
  type?: string;
  metadata?: Record<string, any> | null;
}): string {
  const md = art.metadata || {};
  const filePath = typeof md.file_path === "string" ? md.file_path : "";
  const mdName = typeof md.name === "string" ? md.name : "";
  const baseFromPath = filePath ? filePath.split("/").pop() || filePath : "";
  return (baseFromPath || mdName || art.name || art.type || art.id).trim();
}

export function deriveWorkspaceArtifactFiles(
  feedItems: FeedItem[],
  pinnedArtifactIds: Record<string, boolean>,
  artifactsById: Record<string, { id: string; name?: string; type?: string; metadata?: Record<string, any> | null }> = {},
): ArtifactFileRow[] {
  const codeChanges = feedItems.filter((f) => f.type === "code_change");
  const toolCalls = feedItems.filter(
    (f) => f.type === "tool_call" && f.data.status === "done",
  );

  const seenArtifacts = new Set<string>();
  const artifactFiles: ArtifactFileRow[] = [];

  // ── Backend-emitted artifacts (emit_artifact / propose_refactor /
  //    retrieve_apex / generate_* …). These are the canonical record of
  //    what the agent produced this session; always show them regardless
  //    of pin state so the user can get to the body.
  for (const art of Object.values(artifactsById)) {
    if (!art?.id) continue;
    const name = _artifactDisplayName(art);
    const key = `${art.id}`;
    if (seenArtifacts.has(key)) continue;
    seenArtifacts.add(key);
    const md = art.metadata || {};
    // Map artifact type → metadataType the workspace row expects.
    const mt =
      art.type === "apex_class" ? "ApexClass"
      : art.type === "apex_trigger" ? "ApexTrigger"
      : art.type === "visualforce_page" ? "ApexPage"
      : art.type === "lwc_bundle" ? "LightningComponentBundle"
      : art.type === "aura_bundle" ? "AuraDefinitionBundle"
      : art.type === "flow_definition" ? "Flow"
      : undefined;
    artifactFiles.push({
      id: art.id,
      name,
      metadataType: mt,
      summary: typeof md.summary === "string" ? md.summary : undefined,
      operation: typeof md.operation === "string" ? md.operation : undefined,
    });
  }

  for (const f of toolCalls) {
    if (!FILE_FETCH_TOOLS.has(f.data.name || "")) continue;
    const args = f.data.args || {};
    const fileName =
      args.name ||
      args.class_name ||
      args.file_name ||
      args.object_name ||
      "";
    if (fileName && !seenArtifacts.has(fileName)) {
      const realId = f.data.metadataId || "";
      if (!realId) continue;
      if (!pinnedArtifactIds[realId] && !pinnedArtifactIds[fileName]) continue;
      seenArtifacts.add(fileName);
      const metaType =
        f.data.metadataType ||
        (f.data.name === "get_object_fields" ? "CustomObject" : undefined) ||
        (args.metadata_type as string | undefined);
      artifactFiles.push({ id: realId, name: fileName, metadataType: metaType });
    }
  }

  for (const f of codeChanges) {
    const fn = f.data.fileName || "";
    if (fn && !seenArtifacts.has(fn)) {
      const codeId = f.data.metadataId || "";
      if (!codeId) continue;
      if (!pinnedArtifactIds[codeId] && !pinnedArtifactIds[fn]) continue;
      seenArtifacts.add(fn);
      artifactFiles.push({
        id: codeId,
        name: fn,
        metadataType: f.data.metadataType,
      });
    }
  }

  // Latest-first: backend emits artifacts in chronological order; the
  // workspace panel is more useful when the newest row is at the top.
  return artifactFiles.reverse();
}

export function deriveImpactPanelItems(feedItems: FeedItem[]): FeedItem[] {
  return feedItems
    .filter(
      (f) =>
        f.type === "impact_analysis" ||
        (f.type === "tool_call" &&
          f.data.status === "done" &&
          (f.data.name?.includes("impact") ||
            f.data.name?.includes("graph_dependents") ||
            f.data.name?.includes("graph_calls"))),
    )
    .map((f) => {
      if (f.type === "impact_analysis") {
        return {
          ...f,
          data: {
            ...f.data,
            className: f.data.class_name,
            dependents: f.data.blast_radius,
            callers: f.data.callers,
            riskLevel:
              f.data.blast_radius > 10
                ? "high"
                : f.data.blast_radius > 3
                  ? "medium"
                  : "low",
          },
        };
      }
      let parsed: any = {};
      try {
        parsed =
          typeof f.data.output === "string"
            ? JSON.parse(f.data.output)
            : f.data.output || {};
      } catch {
        /* */
      }
      const args =
        typeof f.data.args === "string"
          ? (() => {
              try {
                return JSON.parse(f.data.args);
              } catch {
                return {};
              }
            })()
          : f.data.args || {};
      return {
        ...f,
        data: {
          ...f.data,
          className:
            args.class_name || args.name || parsed.class_name || f.data.name,
          dependents:
            parsed.dependents_count ||
            parsed.total_dependents ||
            (Array.isArray(parsed.dependents) ? parsed.dependents.length : undefined),
          callers:
            parsed.callers_count ||
            (Array.isArray(parsed.callers) ? parsed.callers.length : undefined),
          riskLevel:
            parsed.risk_level ||
            parsed.risk ||
            (parsed.dependents_count > 10
              ? "high"
              : parsed.dependents_count > 3
                ? "medium"
                : "low"),
        },
      };
    });
}

export function deriveTestPanelItems(feedItems: FeedItem[]): FeedItem[] {
  return feedItems
    .filter(
      (f) =>
        f.type === "test_coverage" ||
        (f.type === "tool_call" &&
          f.data.status === "done" &&
          (f.data.name?.includes("test") ||
            f.data.name?.includes("coverage") ||
            (f.data.name === "tooling_api" && f.data.args?.action === "run_tests"))),
    )
    .map((f) => {
      if (f.type === "test_coverage") {
        return {
          ...f,
          data: {
            ...f.data,
            className: f.data.class_name,
            coveragePct: f.data.aggregate_coverage_pct,
            testClasses: f.data.test_classes,
          },
        };
      }
      let parsed: any = {};
      try {
        parsed =
          typeof f.data.output === "string"
            ? JSON.parse(f.data.output)
            : f.data.output || {};
      } catch {
        /* */
      }
      const args =
        typeof f.data.args === "string"
          ? (() => {
              try {
                return JSON.parse(f.data.args);
              } catch {
                return {};
              }
            })()
          : f.data.args || {};
      return {
        ...f,
        data: {
          ...f.data,
          className:
            args.class_name ||
            args.test_class ||
            args.class_names?.[0] ||
            args.name ||
            parsed.class_name ||
            parsed.test_class_name ||
            "Test",
          coverage: parsed.coverage_percentage || parsed.coverage,
          testMethods:
            parsed.test_methods_count ||
            parsed.num_tests ||
            (Array.isArray(parsed.tests) ? parsed.tests.length : undefined),
          status:
            parsed.outcome ||
            parsed.status ||
            (parsed.num_failures > 0
              ? "failed"
              : parsed.num_tests > 0
                ? "passed"
                : undefined),
          raw_result:
            typeof f.data.output === "string" ? f.data.output : JSON.stringify(parsed, null, 2),
          summary: parsed.summary || parsed.message,
        },
      };
    });
}
